import React from 'react';
import {
  ChevronDown,
  ChevronRight,
  Package,
  MapPin,
  Calendar,
  Hash,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import {
  POrderHistoryLineDto,
  calculateLineCompletionPercentage
} from '../../../../types/purchaseOrderHistory';
import { purchaseOrderHistoryService } from '../../../../services/purchaseOrderHistoryService';
import { StatusBadge } from '../../../common/StatusBadge';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { TransactionRow } from './TransactionRow';

interface LineRowProps {
  line: POrderHistoryLineDto;
  isExpanded: boolean;
  onToggle: () => void;
}

export const LineRow: React.FC<LineRowProps> = ({ line, isExpanded, onToggle }) => {
  const { t } = useTranslation();
  const completionPercentage = calculateLineCompletionPercentage(
    line.quantity,
    line.quantityDelivered
  );

  const isCompleted = purchaseOrderHistoryService.isLineCompleted(line);
  const hasTransactions = line.transactions && line.transactions.length > 0;

  return (
    <div className="bg-white">
      {/* Line Header */}
      <div
        className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start space-x-3">
          {/* Expand/Collapse Icon */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="text-gray-500 hover:text-gray-700 focus:outline-none mt-1"
          >
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>

          {/* Status Icon */}
          <div className={`p-1.5 rounded ${isCompleted ? 'bg-green-100' : 'bg-yellow-100'} mt-0.5`}>
            {isCompleted ? (
              <CheckCircle className="text-green-600" size={18} />
            ) : (
              <AlertCircle className="text-yellow-600" size={18} />
            )}
          </div>

          {/* Line Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900">
                {t('manager.modules.purchaseOrderHistory.lineRow.line', { '0': line.lineNo }, `Linje ${line.lineNo}`)}
              </span>
              {line.lineNoERP && (
                <span className="text-xs text-gray-500">
                  {t('manager.modules.purchaseOrderHistory.lineRow.erpLine', { '0': line.lineNoERP }, `(ERP: ${line.lineNoERP})`)}
                </span>
              )}
              <StatusBadge value={line.lineStatus} statusType={1} />
            </div>

            <div className="flex items-start gap-4 text-sm">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">
                  {line.itemNo}
                  {line.itemBarCode && (
                    <span className="ml-2 text-gray-500">({line.itemBarCode})</span>
                  )}
                </p>
                {line.description1 && (
                  <p className="text-gray-600 text-sm mt-0.5 truncate">
                    {line.description1}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
                  {line.stdLocationName && (
                    <span className="flex items-center">
                      <MapPin className="mr-1" size={12} />
                      {line.stdLocationName}
                    </span>
                  )}
                  {line.itemBatchNo && (
                    <span className="flex items-center">
                      <Hash className="mr-1" size={12} />
                      {t('manager.modules.purchaseOrderHistory.lineRow.batch', { '0': line.itemBatchNo }, `Batch: ${line.itemBatchNo}`)}
                    </span>
                  )}
                  {line.deliveryDate && (
                    <span className="flex items-center">
                      <Calendar className="mr-1" size={12} />
                      {purchaseOrderHistoryService.formatDate(line.deliveryDate)}
                    </span>
                  )}
                  {hasTransactions && (
                    <span className="text-blue-600 font-medium">
                      {line.transactions.length !== 1
                        ? t('manager.modules.purchaseOrderHistory.lineRow.transactionCountPlural', { '0': line.transactions.length }, `${line.transactions.length} transaksjoner`)
                        : t('manager.modules.purchaseOrderHistory.lineRow.transactionCountSingular', { '0': line.transactions.length }, `${line.transactions.length} transaksjon`)}
                    </span>
                  )}
                </div>
              </div>

              {/* Quantity Info */}
              <div className="text-right min-w-[120px]">
                <div className="text-sm">
                  <span className="font-semibold text-gray-900">
                    {line.quantityDelivered || 0}
                  </span>
                  <span className="text-gray-500"> / </span>
                  <span className="text-gray-700">{line.quantity || 0}</span>
                  {line.itemUnitCode && (
                    <span className="text-gray-500 ml-1">{line.itemUnitCode}</span>
                  )}
                </div>
                {line.remainingQuantity > 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    {t('manager.modules.purchaseOrderHistory.lineRow.remaining', { '0': line.remainingQuantity }, `${line.remainingQuantity} gjenstår`)}
                  </p>
                )}

                {/* Progress Bar */}
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${
                      completionPercentage === 100 ? 'bg-green-500' : 'bg-yellow-500'
                    }`}
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">{completionPercentage}%</p>
              </div>
            </div>

            {/* Price Info */}
            {line.price !== undefined && line.price !== null && (
              <div className="mt-2 text-xs text-gray-600">
                {t('manager.modules.purchaseOrderHistory.lineRow.price', { '0': purchaseOrderHistoryService.formatCurrency(line.price) }, `Pris: ${purchaseOrderHistoryService.formatCurrency(line.price)}`)}
                {line.quantity && (
                  <span className="ml-2">
                    {t('manager.modules.purchaseOrderHistory.lineRow.priceTotal', { '0': purchaseOrderHistoryService.formatCurrency(line.price * line.quantity) }, `(Total: ${purchaseOrderHistoryService.formatCurrency(line.price * line.quantity)})`)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Transactions */}
      {isExpanded && hasTransactions && (
        <div className="pl-12 pr-4 pb-3 bg-gray-50">
          <div className="bg-white rounded border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 bg-gray-100 border-b border-gray-200">
              <h5 className="text-xs font-semibold text-gray-700">
                {t('manager.modules.purchaseOrderHistory.common.transactionsCount', { '0': line.transactions.length }, `Transaksjoner (${line.transactions.length})`)}
              </h5>
            </div>
            <div className="divide-y divide-gray-200">
              {line.transactions.map((transaction, index) => (
                <TransactionRow key={transaction.id || index} transaction={transaction} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* No transactions message */}
      {isExpanded && !hasTransactions && (
        <div className="pl-12 pr-4 pb-3">
          <div className="px-3 py-4 text-center text-gray-500 bg-gray-50 rounded border border-gray-200">
            <Package className="mx-auto mb-1 text-gray-400" size={20} />
            <p className="text-xs">{t('manager.modules.purchaseOrderHistory.lineRow.noTransactions', {}, 'Ingen transaksjoner registrert')}</p>
          </div>
        </div>
      )}
    </div>
  );
};
